import { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { API, apiErr } from "@/lib/api";
import getCroppedImg from "@/lib/cropImage";
import { Upload, Trash2, ImageIcon, ZoomIn } from "lucide-react";

export function ImageUpload({ value, onChange, aspect = 16 / 9, label = "Imagem", testid = "image", previewClass = "h-40" }) {
  const fileRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onCropComplete = useCallback((_area, areaPx) => setAreaPixels(areaPx), []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecione um arquivo de imagem"); return; }
    const reader = new FileReader();
    reader.onload = () => { setSrc(reader.result); setCrop({ x: 0, y: 0 }); setZoom(1); setOpen(true); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const confirm = async () => {
    if (!areaPixels) return;
    try {
      setUploading(true);
      const blob = await getCroppedImg(src, areaPixels);
      const fd = new FormData();
      fd.append("file", blob, "upload.jpg");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${API}/files/${data.path}`);
      setOpen(false); setSrc(null);
      toast.success("Imagem enviada");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Falha ao enviar a imagem"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      {label && <Label className="text-zinc-300">{label}</Label>}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-file-input`} />
      <div className="mt-1 flex items-start gap-3">
        <div className={`${previewClass} w-full rounded-xl overflow-hidden border border-zinc-700 bg-zinc-800 flex items-center justify-center shrink-0`}>
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          ) : (
            <div className="text-zinc-600 flex flex-col items-center text-xs"><ImageIcon className="w-7 h-7 mb-1" />Sem imagem</div>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <Button type="button" size="sm" variant="outline" className="border-zinc-700 text-zinc-200" onClick={() => fileRef.current?.click()} data-testid={`${testid}-upload-btn`}>
          <Upload className="w-4 h-4 mr-1" /> {value ? "Trocar imagem" : "Enviar da galeria"}
        </Button>
        {value && (
          <Button type="button" size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => onChange("")} data-testid={`${testid}-remove-btn`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!uploading) setOpen(v); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader><DialogTitle className="text-white">Ajustar imagem</DialogTitle></DialogHeader>
          <div className="relative w-full h-72 bg-black rounded-xl overflow-hidden">
            {src && (
              <Cropper
                image={src}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <ZoomIn className="w-4 h-4 text-zinc-400" />
            <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={(v) => setZoom(v[0])} className="flex-1" data-testid={`${testid}-zoom`} />
          </div>
          <p className="text-xs text-zinc-500">Arraste para posicionar e use o controle para dar zoom. A área visível será recortada.</p>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setOpen(false)} disabled={uploading}>Cancelar</Button>
            <Button onClick={confirm} disabled={uploading} className="bg-amber-400 text-black font-bold" data-testid={`${testid}-confirm-btn`}>
              {uploading ? "Enviando..." : "Recortar e salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
